/* eslint-disable react-hooks/exhaustive-deps */

import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Paper from "@material-ui/core/Paper";
import {useMutation, useQuery} from "@apollo/react-hooks";
import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import {WithContext as ReactTags} from 'react-tag-input';
import Typography from "@material-ui/core/Typography";
import {CREATE_ACCOUNT, GET_ACCOUNT, UPDATE_ACCOUNT} from "../../database/queries/accountQueries";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import BackupOutlinedIcon from "@material-ui/icons/BackupOutlined";
import ClearIcon from "@material-ui/icons/Clear";
import {notify} from "../../utils/notify";
import usePersistentStore from "../../persistentStore";
import FormHelperText from "@material-ui/core/FormHelperText";
import {useHistory, useLocation} from "react-router-dom";
import {tagDelimiters} from "../../utils/utils";

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
        '& .ReactTags__tagInputField': {
            paddingLeft: 5,
            paddingRight: 5,
        },
        '& p': {
            color: 'red',
        }
    },
    gridContainer: {
        overflow: 'auto',
        overflowY: 'scroll',
        padding: 20,
    },
    formControl: {
        'input': {
            '&::placeholder': {
                color: 'blue'
            }
        },
        '& .MuiFormLabel-root': {
            color: '#a0aec0'
        },
    },
    formControlError: {
        'input': {
            '&::placeholder': {
                color: 'red'
            }
        },
        '& .MuiFormLabel-root': {
            color: 'red'
        },
        '& .MuiInput-underline:before': {
            borderColor: 'red'
        },
    },
    leftField: {
        paddingRight: 20
    },
    rightField: {
        paddingLeft: 20,
    },
    hidden: {
        display: 'none',
    },
    grow: {
        flexGrow: 1,
    },
    title: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
        marginRight: 10,
    },
    noMargin: {
        marginBottom: 0,
        minHeight: 0
    },
    multiline: {
        maxHeight: 100,
        overflowY: 'scroll'
    }
}));

const AccountEdit = props => {
    const classes = useStyles();
    const [account, setAccount] = useState({});
    const [tagSuggestions] = useState([]);
    const [errors, setErrors] = useState({});
    const [globalPersistentState, globalPersistentActions] = usePersistentStore();
    let location = useLocation();
    const history = useHistory();

    const {loading: getAccountLoading, error: getAccountError, data: getAccountData, refetch: getAccountRefetch} = useQuery(GET_ACCOUNT, {
        skip: !props.editId || props.editId === 'new',
        variables: {accountId: props.editId},
    });

    const [updateAccount, {error: updateAccountError}] = useMutation(UPDATE_ACCOUNT, {
        variables: {
            changedAccount: {
                accountId: account.accountId,
                accountName: account.accountName,
                accountLinkedinId: account.accountLinkedinId,
                accountDescription: account.accountDescription,
                tags: account.tags
            }
        }
    });

    const [createAccount, {error: createAccountError}] = useMutation(CREATE_ACCOUNT, {
        variables: {
            newAccount: {
                accountName: account.accountName,
                accountDescription: account.accountDescription,
                accountLinkedinId: account.accountLinkedinId,
                tags: account.tags
            }
        }
    });

    useEffect(() => {
        if (createAccountError) notify('createAccountError', 'error', createAccountError.message);
        if (getAccountError) notify('getAccountError', 'error', getAccountError.message);
        if (updateAccountError) notify('updateAccountError', 'error', updateAccountError.message);
    }, [createAccountError, getAccountError, updateAccountError]);

    useEffect(() => {
        if (getAccountData && getAccountData.account) {
            let fetchedAccount = {...getAccountData.account};

            if (getAccountData.account.tagLinks) {
                let tags = [];
                getAccountData.account.tagLinks.map((tagLink) => {
                    const tag = {id: tagLink.tagId, text: tagLink.tag.tag};
                    tags.push(tag);
                    return tag;
                });
                fetchedAccount.tags = tags;
            }
            setAccount({...fetchedAccount});
            if (globalPersistentState.dirty) globalPersistentActions.setDirty(false);
        }
    }, [getAccountData]);

    useEffect(() => {
    }, [account]);

    const handleChange = (e) => {
        const updatedAccount = {
            ...account,
            [e.target.name]: e.target.value
        };
        setAccount(updatedAccount);
        if (!globalPersistentState.dirty) globalPersistentActions.setDirty(true);
    };

    const handleTagAddition = (newTag) => {
        if (!account.tags) account.tags = [];
        const tags = [...account.tags, newTag];
        const updatedAccount = {
            ...account,
            tags: tags
        };
        setAccount(updatedAccount);
        if (!globalPersistentState.dirty) globalPersistentActions.setDirty(true);
    };

    const handleTagDelete = (i) => {
        const tags = account.tags.filter((tag, index) => index !== i);
        const updatedAccount = {
            ...account,
            tags: tags
        };
        setAccount(updatedAccount);
        if (!globalPersistentState.dirty) globalPersistentActions.setDirty(true);
    };

    const validateForm = () => {
        setErrors({});
        let newError = {};
        if (!account.accountName || account.accountName.length < 3) newError.accountName = 'Account name must be at least 3 characters long';

        if (Object.keys(newError).length > 0) {
            setErrors(newError);
            return false;
        } else {
            return true;
        }
    };

    if (getAccountLoading) return <p>Loading ...</p>;

    return (
        <form className={classes.root}>
            {account && (
                <div>
                    <Paper className={classes.textboxPaper}>
                        <AppBar color="default" position="static" className={classes.noMargin}>
                            <Toolbar className={classes.noMargin}>
                                <div className={classes.grow}/>
                                {globalPersistentState.dirty && <IconButton
                                    aria-label="save account"
                                    id={"save-account"}
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        if (validateForm()) {
                                            if (account.accountId && account.accountId !== 'new') {
                                                updateAccount().then(function (updateResult) {
                                                    notify('updateAccount', 'info', 'Account updated');
                                                    setAccount(account);
                                                    if (globalPersistentState.dirty) globalPersistentActions.setDirty(false);
                                                    getAccountRefetch();
                                                })
                                            } else {
                                                createAccount().then(function (createResult) {
                                                    notify('createAccount', 'info', 'Account created');
                                                    const newLoc = location.pathname.replace('/new', '/' + createResult.data.createAccount.id);
                                                    console.log('## SVR accountEdit ## changing history', newLoc);
                                                    history.push(newLoc);
                                                })
                                            }
                                        }
                                    }}>
                                    <BackupOutlinedIcon/>
                                </IconButton>}
                                {globalPersistentState.dirty && <IconButton
                                    aria-label="cancel edit account"
                                    id={"cancel-save-account"}
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        if (globalPersistentState.dirty) globalPersistentActions.setDirty(false);
                                        getAccountRefetch()
                                    }}>
                                    <ClearIcon/>
                                </IconButton>}
                            </Toolbar>
                        </AppBar>

                        <Grid container className={classes.gridContainer}>
                            <Grid item xs={6}>
                                <FormControl
                                    className={errors.accountName ? classes.formControlError : classes.formControl}
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                >
                                    <TextField
                                        required
                                        id="accountName"
                                        name="accountName"
                                        label="Account Name"
                                        fullWidth
                                        size="small"
                                        onChange={handleChange}
                                        value={account.accountName ? account.accountName : ''}
                                    />
                                    <FormHelperText>
                                        {errors.accountName}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} className={classes.rightField}>
                                <FormControl
                                    className={classes.formControl}
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                >
                                    <TextField
                                        id="accountLinkedinId"
                                        name="accountLinkedinId"
                                        label="Linkedin Id"
                                        fullWidth
                                        size="small"
                                        disabled={account.accountLinkedinId !== null}
                                        onChange={handleChange}
                                        value={account.accountLinkedinId ? account.accountLinkedinId : ''}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl
                                    className={classes.formControl}
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                >
                                    <TextField
                                        id="accountDescription"
                                        name="accountDescription"
                                        label="Account description"
                                        className={classes.multiline}
                                        fullWidth
                                        size="small"
                                        multiline
                                        value={account.accountDescription ? account.accountDescription : ''}
                                        onChange={handleChange}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl
                                    className={classes.formControl}
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                >
                                    <Typography variant="subtitle2" color="inherit">
                                        Tags
                                    </Typography>
                                    <Paper>
                                        <ReactTags
                                            tags={account.tags}
                                            suggestions={tagSuggestions}
                                            handleDelete={handleTagDelete}
                                            handleAddition={handleTagAddition}
                                            delimiters={tagDelimiters}/>
                                    </Paper>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>
                </div>
            )}
        </form>
    )
};

export default AccountEdit
